import { cn } from '@/shadcn/lib/utils';

import { CodeEditorReaderWidget } from '@/components/forms/components/form/reader/widgets/codeEditorWidget';
import { isLockedWidget } from '@/components/forms/utils/links';
import { CodeEditor } from '@/components/monaco/codeEditor/codeEditor';
import { useDynamicProps } from '@/hooks/useDynamicProps';
import { TestId } from '@/types/testId';
import type { WidgetWriterProps } from '../widget.type';

type Props = WidgetWriterProps & {
  /** Language for syntax highlighting - defaults to 'json' */
  language?: string;
  /** Height of the editor - defaults to '300px' */
  height?: React.CSSProperties['height'];
  /** Additional options passed via uiSchema */
  options?: {
    language?: string;
    height?: React.CSSProperties['height'];
  };
};

export const CodeEditorWidget = (rjsfProps: Props) => {
  const props = useDynamicProps(rjsfProps);

  const isLocked = isLockedWidget(props.id, props.formContext?.links);

  // If locked, redirect to reader widget
  if (isLocked) {
    return (
      <div className="opacity-50">
        <CodeEditorReaderWidget
          label={props.label}
          name={props.name}
          options={{
            language: props.language || props.options?.language,
            height: props.height || props.options?.height
          }}
          value={props.value}
        />
      </div>
    );
  }

  const language = props.language || props.options?.language || 'json';
  const height = props.height || props.options?.height || '300px';

  return (
    <div
      className={cn(
        'w-full rounded-md',
        props.invalid && 'ring-2 ring-destructive/70'
      )}
      data-cy={`code-editor-${props.name}`}
      data-testid={TestId.FormWriter.CodeEditor.Container}
    >
      <CodeEditor
        className={cn('rounded-md', props.invalid && 'border-destructive')}
        height={height}
        language={language}
        onChange={props.onChange}
        value={props.value ?? ''}
      />
    </div>
  );
};
